import { DEFAULT_TIMEZONE } from '@/config';

import type { Locale } from './index';

const ARABIC_INDIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'] as const;

/** Convert Western digits to Arabic-Indic when the user prefers them. */
export function localizeDigits(input: string, useArabicIndic: boolean): string {
  if (!useArabicIndic) return input;
  return input.replace(/[0-9]/g, (digit) => ARABIC_INDIC_DIGITS[Number(digit)]);
}

export interface NumeralOptions {
  locale: Locale;
  arabicNumerals: boolean;
}

/**
 * Numeral options that ALWAYS render Latin (Western) digits, ignoring the user's
 * Arabic-numerals preference. Used where tabular alignment matters most -
 * leaderboards, ranks, points - so columns stay tidy in either locale.
 */
export function latinNumerals(locale: Locale): NumeralOptions {
  return { locale, arabicNumerals: false };
}

/**
 * Memoized `Intl` factories. Constructing an `Intl.NumberFormat` /
 * `Intl.DateTimeFormat` is surprisingly expensive, and these are called once per
 * cell on 300+ row leaderboards. We cache instances keyed by locale+options so we
 * pay the construction cost once per distinct shape, not per render.
 */
const numberFormatCache = new Map<string, Intl.NumberFormat>();
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();

function numberFormat(numberLocale: string): Intl.NumberFormat {
  let instance = numberFormatCache.get(numberLocale);
  if (!instance) {
    instance = new Intl.NumberFormat(numberLocale);
    numberFormatCache.set(numberLocale, instance);
  }
  return instance;
}

function dateTimeFormat(
  formatLocale: string,
  opts: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${formatLocale}|${JSON.stringify(opts)}`;
  let instance = dateTimeFormatCache.get(key);
  if (!instance) {
    instance = new Intl.DateTimeFormat(formatLocale, opts);
    dateTimeFormatCache.set(key, instance);
  }
  return instance;
}

export function formatNumber(value: number, options: NumeralOptions): string {
  const formatted = numberFormat(options.locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-US')
    .format(value);
  return localizeDigits(formatted, options.arabicNumerals);
}

/** Fantasy money: 5.5 → "5.5" (the "m" suffix comes from the locale string). */
export function formatPrice(value: number, options: NumeralOptions): string {
  return localizeDigits(value.toFixed(1).replace(/\.0$/, '.0'), options.arabicNumerals);
}

function dtf(locale: Locale, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return dateTimeFormat(locale === 'ar' ? 'ar' : 'en-GB', {
    timeZone: DEFAULT_TIMEZONE,
    ...opts,
  });
}

/** Hour-of-day (0-23) for a date in the app timezone - drives AM/PM choice. */
function hourInTimezone(date: Date): number {
  const value = dateTimeFormat('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    hour: '2-digit',
    hour12: false,
  }).format(date);
  const hour = Number(value);
  return hour === 24 ? 0 : hour; // some engines render midnight as "24"
}

/**
 * Kickoff time in 12-hour format with a localized day-period.
 * EN → "7:00 PM"; AR → "٧:٠٠ مساءً" (full صباحًا/مساءً words, digits follow
 * the Arabic-numerals preference). The whole app uses 12h, never 24h.
 */
export function formatKickoffTime(date: Date, options: NumeralOptions): string {
  const parts = dateTimeFormat('en-GB', {
    timeZone: DEFAULT_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '';
  const time = localizeDigits(`${hour}:${minute}`, options.arabicNumerals);
  const isMorning = hourInTimezone(date) < 12;
  if (options.locale === 'ar') {
    return `${time} ${isMorning ? 'صباحًا' : 'مساءً'}`;
  }
  return `${time} ${isMorning ? 'AM' : 'PM'}`;
}

export function formatMatchDate(date: Date, options: NumeralOptions): string {
  const formatted = dtf(options.locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
  return localizeDigits(formatted, options.arabicNumerals);
}

export function formatFullDate(date: Date, options: NumeralOptions): string {
  const day = dtf(options.locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(date);
  // Time portion reuses the 12-hour kickoff formatter (AM/PM · صباحًا/مساءً).
  return `${localizeDigits(day, options.arabicNumerals)} · ${formatKickoffTime(date, options)}`;
}

/**
 * A relative-time descriptor for "x ago" labels (news cards). Returns an i18n
 * key + count so the caller renders it via `t()` (kept out of this util to stay
 * locale-string-free). Buckets: <1 min → now, <60 min → minutes, <24 h → hours,
 * <7 d → days, otherwise null (caller falls back to an absolute date).
 */
export function relativeTimeParts(
  date: Date,
  now: Date = new Date(),
): { key: 'now' | 'minutes' | 'hours' | 'days'; count: number } | null {
  const diffMs = now.getTime() - date.getTime();
  if (!Number.isFinite(diffMs)) return null;
  const seconds = Math.max(0, Math.floor(diffMs / 1000));
  if (seconds < 60) return { key: 'now', count: 0 };
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return { key: 'minutes', count: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: 'hours', count: hours };
  const days = Math.floor(hours / 24);
  if (days < 7) return { key: 'days', count: days };
  return null;
}

/** Transfer date: "11 Jun 2026" / "١١ يونيو ٢٠٢٦" - day, month, year, no weekday. */
export function formatTransferDate(date: Date, options: NumeralOptions): string {
  const formatted = dtf(options.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
  return localizeDigits(formatted, options.arabicNumerals);
}

/** Key of the calendar day in the app timezone, e.g. "2026-06-11". */
export function dayKey(date: Date): string {
  const parts = dateTimeFormat('en-CA', {
    timeZone: DEFAULT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
  return parts;
}

export function isSameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

function tzOffsetMs(date: Date, timeZone: string): number {
  const parts = dateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  return asUtc - date.getTime();
}

/**
 * UTC bounds [start, end) of a calendar day (`YYYY-MM-DD`) in the app
 * timezone - DST-safe (Africa/Cairo observes DST again since 2023).
 */
export function dayBoundsUtc(key: string): { start: Date; end: Date } {
  const naive = new Date(`${key}T00:00:00Z`);
  let start = new Date(naive.getTime() - tzOffsetMs(naive, DEFAULT_TIMEZONE));
  const refined = tzOffsetMs(start, DEFAULT_TIMEZONE);
  start = new Date(naive.getTime() - refined);
  return { start, end: new Date(start.getTime() + 24 * 60 * 60 * 1000) };
}

export function addDays(date: Date, days: number): Date {
  const copy = new Date(date.getTime());
  copy.setDate(copy.getDate() + days);
  return copy;
}
